/**
 * TeamAvatar — fallback avatar elegante con iniziali su gradient brand color.
 * Si attiva via `onError` quando l'<img> primaria fallisce (TLS/hotlink/404).
 * Stile: cerchio/card con iniziali bianche su sfondo verde Colucci, ombra soft.
 */
import { useState } from "react";
import useTeamPhotos from "@/hooks/useTeamPhotos";

const BG_BY_INITIAL = {
  // mapping leggero per dare varietà cromatica (palette brand)
  default: "#163321",
  A: "#163321", B: "#1F4030", C: "#163321", D: "#264A38",
  E: "#1F4030", F: "#163321", G: "#264A38", H: "#1F4030",
  I: "#163321", J: "#1F4030", K: "#264A38", L: "#163321",
  M: "#1F4030", N: "#163321", O: "#264A38", P: "#1F4030",
  Q: "#163321", R: "#264A38", S: "#1F4030", T: "#163321",
  U: "#264A38", V: "#1F4030", W: "#163321", X: "#264A38",
  Y: "#1F4030", Z: "#163321",
};

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((s) => s[0]?.toUpperCase() || "").join("");
}

export default function TeamAvatar({ name, role, src, className = "", imgClassName = "" }) {
  const [errored, setErrored] = useState(false);
  const { findPhoto } = useTeamPhotos();
  // Priorità foto: 1) foto admin uploadata (WebP locale), 2) src esterno passato dal parent
  const adminPhoto = findPhoto(name);
  const effectiveSrc = adminPhoto || src;
  if (!errored && effectiveSrc) {
    return (
      <img
        src={effectiveSrc}
        alt={name}
        className={imgClassName}
        loading="lazy"
        onError={() => setErrored(true)}
        data-testid={`team-photo-${(name || "").toLowerCase().replace(/\s+/g, "-")}`}
      />
    );
  }
  const ini = initials(name);
  const bg = BG_BY_INITIAL[ini[0]] || BG_BY_INITIAL.default;
  return (
    <div
      className={`w-full h-full flex items-center justify-center text-white ${className}`}
      style={{ background: `linear-gradient(135deg, ${bg} 0%, ${bg}cc 100%)` }}
      role="img"
      aria-label={name}
      data-testid={`team-avatar-fallback-${(name || "").toLowerCase().replace(/\s+/g, "-")}`}
    >
      <span className="font-heading font-bold tracking-tight" style={{ fontSize: "clamp(2rem, 6vw, 4rem)" }}>
        {ini}
      </span>
    </div>
  );
}
